/**
 * Secure in-memory authentication storage
 *
 * Tokens are stored in-memory only to prevent XSS exfiltration via localStorage.
 * On page refresh, users must re-authenticate. This is acceptable because:
 * 1. Access tokens have 1-day TTL (short-lived)
 * 2. Refresh tokens are rotated and revoked on use (Task 1)
 * 3. XSS protection is more critical than UX convenience
 *
 * Fallback: sessionStorage is used only as a temporary bridge during auth flow.
 * It is cleared immediately after tokens are moved to memory.
 */

import type { StoredExtensionAuth } from '../../types/api';
import { createLogger } from '../../shared/logger';

const log = createLogger('portal:secure-auth-storage');

// In-memory auth cache (lost on page refresh, not exposed to XSS)
let authCache: StoredExtensionAuth | null = null;

/**
 * Store auth tokens in secure in-memory cache
 * XSS cannot access this unless the attacker gains code execution in the same context
 */
export function storeAuthInMemory(authData: StoredExtensionAuth): void {
  authCache = { ...authData };
  log.info('[secure-auth] Auth stored in memory');
}

/**
 * Retrieve auth from in-memory cache
 */
export function loadAuthFromMemory(): StoredExtensionAuth | null {
  return authCache ? { ...authCache } : null;
}

/**
 * Clear in-memory auth cache
 */
export function clearAuthFromMemory(): void {
  authCache = null;
  log.info('[secure-auth] Auth cleared from memory');
}

/**
 * Check if auth is currently in memory
 */
export function isAuthInMemory(): boolean {
  return authCache !== null;
}

/**
 * Temporary sessionStorage bridge for auth flow
 * This is used ONLY during the authentication flow to pass data between redirects.
 * It must be cleared after auth is loaded into memory.
 */
const SESSION_STORAGE_KEY = 'lotlingo.portal.auth.session.v1';
const SESSION_STORAGE_EXPIRY_KEY = 'lotlingo.portal.auth.session.expiry.v1';

/**
 * Store auth temporarily in sessionStorage during auth flow
 * This is a temporary measure during redirect flows (e.g., PKCE exchange)
 */
export function storeAuthInSessionStorage(authData: StoredExtensionAuth): void {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(authData));
    // Mark when it should be cleared (5 minutes from now)
    sessionStorage.setItem(SESSION_STORAGE_EXPIRY_KEY, (Date.now() + 5 * 60 * 1000).toString());
    log.info('[secure-auth] Auth stored in sessionStorage (temporary bridge)');
  } catch (err) {
    log.warn('[secure-auth] Failed to store auth in sessionStorage:', err);
  }
}

/**
 * Load auth from sessionStorage (during auth flow recovery)
 * Must be immediately moved to memory and cleared
 */
export function loadAuthFromSessionStorage(): StoredExtensionAuth | null {
  try {
    const expiryStr = sessionStorage.getItem(SESSION_STORAGE_EXPIRY_KEY);
    const expiry = expiryStr ? Number(expiryStr) : 0;

    // Check if sessionStorage data has expired
    if (Date.now() > expiry) {
      clearAuthFromSessionStorage();
      return null;
    }

    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (raw) {
      return JSON.parse(raw) as StoredExtensionAuth;
    }
  } catch (err) {
    log.warn('[secure-auth] Failed to load auth from sessionStorage:', err);
  }
  return null;
}

/**
 * Clear auth from sessionStorage after moving to memory
 * MUST be called after loadAuthFromSessionStorage + storeAuthInMemory
 */
export function clearAuthFromSessionStorage(): void {
  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    sessionStorage.removeItem(SESSION_STORAGE_EXPIRY_KEY);
    log.info('[secure-auth] SessionStorage auth cleared');
  } catch (err) {
    log.warn('[secure-auth] Failed to clear sessionStorage:', err);
  }
}

/**
 * Migrate auth from sessionStorage to in-memory cache
 * Call this once during boot to recover auth from redirect flows
 */
export function migrateAuthFromSessionToMemory(): StoredExtensionAuth | null {
  const sessionAuth = loadAuthFromSessionStorage();
  if (sessionAuth) {
    storeAuthInMemory(sessionAuth);
    clearAuthFromSessionStorage();
    log.info('[secure-auth] Auth migrated from sessionStorage to memory');
    return authCache;
  }
  return null;
}

/**
 * Cleanup: clear any stale sessionStorage entries on startup
 * This helps prevent data leaks if logout was incomplete
 */
export function cleanupStaleSessionStorage(): void {
  try {
    const expiryStr = sessionStorage.getItem(SESSION_STORAGE_EXPIRY_KEY);
    if (expiryStr && Date.now() > Number(expiryStr)) {
      clearAuthFromSessionStorage();
      log.info('[secure-auth] Cleared stale sessionStorage entry');
    }
  } catch (err) {
    log.warn('[secure-auth] Failed to cleanup stale sessionStorage:', err);
  }
}
